import React, { useMemo } from "react";
import { useSiteIdParam } from "../../../../context/SitesContext";
import {
  LegacyFloorWithSpaces,
  useGetFloors,
} from "../../../../global_functions/fortyTwoApi/tenantsAndSpaces/hooks/useGetFloors";
import { Space } from "../../../../global_functions/fortyTwoApi/tenantsAndSpaces/types";
import { useUnsavedChangesWarning } from "../../../../global_functions/useUnsavedChangesWarning";
import { useGetPropertyUseRollup } from "../../../../global_functions/fortyTwoApi/tenantsAndSpaces/hooks/useGetPropertyUseRollup";
import { hasBuildingCompletedTenantsAndSpaces } from "./TenantsAndSpaces/utils";

type FloorOrSpaceId = number | string | null;

export interface TenantsAndSpacesContextType {
  selectedFloor: LegacyFloorWithSpaces | null;
  setSelected: (
    selected: Partial<{
      floorId: FloorOrSpaceId;
      spaceId: FloorOrSpaceId;
    }>,
    didCancel?: boolean
  ) => Promise<void>;
  selectedSpace: Space | null;
  formIsPristine: boolean;
  setFormIsPristine: React.Dispatch<React.SetStateAction<boolean>>;
  propertyUseQueryStatuses: {
    propertyUseFlowCompleted: boolean;
    propertyUseLoading: boolean;
    propertyUseIsInitialLoadError: boolean;
  };
  tenantsAndSpacesIsCompleted: boolean;
}

export const TenantsAndSpacesContext =
  React.createContext<TenantsAndSpacesContextType>({
    selectedFloor: null,
    setSelected: async () => {},
    selectedSpace: null,
    // pulling the following form values into context instead of wrapping entire page inside the Form component
    // react-final-form rerenders all children on every input change by default, which would be a ton of rerenders for buildings with lots of floors/spaces
    // we could potentially use final form's subscripton feature to save renders, but it seemed simpler to just pull out the values we need into context
    formIsPristine: true,
    setFormIsPristine: () => {},
    // unsaved changes warning
    propertyUseQueryStatuses: {
      propertyUseFlowCompleted: false,
      propertyUseLoading: false,
      propertyUseIsInitialLoadError: false,
    },
    tenantsAndSpacesIsCompleted: false,
  });

export const useTenantsAndSpacesContext = () => {
  return React.useContext(TenantsAndSpacesContext);
};

export const TenantsAndSpacesContextProvider = ({
  children,
}: {
  readonly children: React.ReactNode;
}) => {
  const siteId = useSiteIdParam();
  const [selectedFloorId, setSelectedFloorId] =
    React.useState<FloorOrSpaceId>(null);
  const [selectedSpaceId, setSelectedSpaceId] =
    React.useState<FloorOrSpaceId>(null);
  const [formIsPristine, setFormIsPristine] = React.useState(true);
  const [propertyUseIsInitialLoadError, setPropertyUseIsInitialLoadError] =
    React.useState(false);

  const { data: floorsResponse } = useGetFloors(siteId, {
    includeEquipment: true,
  });

  const selectedSpace = useMemo(
    () =>
      floorsResponse?.floors
        .find((floor) => floor.id === selectedFloorId)
        ?.spaces.find((space) => space.id === selectedSpaceId) ?? null,
    [floorsResponse, selectedFloorId, selectedSpaceId]
  );

  // selectedFloor stays in sync with query data
  // if rentable floor area is updated on the selectedFloor, that information will be immediately available in the context value
  const selectedFloor = React.useMemo(
    () =>
      (floorsResponse?.floors ?? []).find(
        (floor) => floor.id === selectedFloorId
      ) ?? null,
    [selectedFloorId, floorsResponse]
  );

  // reset context pristine form state if floor and space are null
  React.useEffect(() => {
    if (selectedFloor === null && selectedSpace === null) {
      setFormIsPristine(true);
    }
  }, [selectedFloor, selectedSpace]);

  const { unsavedChangesPrompts, confirmDiscardUnsavedChanges } =
    useUnsavedChangesWarning({
      when: !formIsPristine,
    });

  const setSelected = React.useCallback(
    async (
      selected: Partial<{
        floorId: FloorOrSpaceId;
        spaceId: FloorOrSpaceId;
      }>,
      didCancel?: boolean
    ) => {
      const shouldSkipUnsavedChangesModal =
        formIsPristine || selectedFloorId === null || !didCancel;
      if (
        shouldSkipUnsavedChangesModal ||
        (await confirmDiscardUnsavedChanges())
      ) {
        selected.floorId !== undefined && setSelectedFloorId(selected.floorId);
        selected.spaceId !== undefined && setSelectedSpaceId(selected.spaceId);
      }
    },
    [formIsPristine, selectedFloorId, confirmDiscardUnsavedChanges]
  );

  const tenantsAndSpacesIsCompleted = !!hasBuildingCompletedTenantsAndSpaces(
    floorsResponse?.floors
  );

  const propertyUseQuery = useGetPropertyUseRollup(siteId);

  // @ts-expect-error
  const propertyUseFlowCompleted = propertyUseQuery.error?.response?.statusCode !== 404 && (propertyUseQuery.data?.propertyUses ?? []).length > 0; // prettier-ignore

  React.useEffect(() => {
    if (
      // @ts-expect-error
      propertyUseQuery.error?.response?.statusCode === 404 &&
      propertyUseQuery.errorUpdateCount >= 1
    ) {
      setPropertyUseIsInitialLoadError(true);
    }
    // if property use is completed, set to false
    if (propertyUseFlowCompleted) {
      setPropertyUseIsInitialLoadError(false);
    }
  }, [
    propertyUseFlowCompleted,
    propertyUseQuery.error,
    propertyUseQuery.errorUpdateCount,
  ]);

  const val = React.useMemo(
    () => ({
      selectedFloor,
      setSelected,
      selectedSpace,
      formIsPristine,
      setFormIsPristine,
      propertyUseQueryStatuses: {
        propertyUseFlowCompleted,
        propertyUseLoading: propertyUseQuery.isLoading,
        propertyUseIsInitialLoadError,
      },
      tenantsAndSpacesIsCompleted,
    }),
    [
      selectedFloor,
      setSelected,
      selectedSpace,
      formIsPristine,
      propertyUseFlowCompleted,
      propertyUseQuery.isLoading,
      propertyUseIsInitialLoadError,
      tenantsAndSpacesIsCompleted,
    ]
  );

  return (
    <TenantsAndSpacesContext.Provider value={val}>
      {unsavedChangesPrompts}
      {children}
    </TenantsAndSpacesContext.Provider>
  );
};
